import { cn } from "@thilke/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

const counterVariants = cva("grid gap-1.5", {
  variants: {
    orientation: {
      horizontal: "",
      vertical: "w-fit",
    },
  },
  defaultVariants: {
    orientation: "horizontal",
  },
});

const counterPipVariants = cva(
  "shrink-0 rounded-[calc(var(--radius)-2px)] border transition-colors",
  {
    variants: {
      size: {
        sm: "size-2.5",
        default: "size-3.5",
        lg: "size-5",
      },
      tone: {
        default:
          "data-[filled=true]:border-primary data-[filled=true]:bg-primary",
        accent: "data-[filled=true]:border-accent data-[filled=true]:bg-accent",
        success:
          "data-[filled=true]:border-success data-[filled=true]:bg-success",
        warning:
          "data-[filled=true]:border-warning data-[filled=true]:bg-warning",
        destructive:
          "data-[filled=true]:border-destructive data-[filled=true]:bg-destructive",
        info: "data-[filled=true]:border-info data-[filled=true]:bg-info",
        muted:
          "data-[filled=true]:border-muted-foreground data-[filled=true]:bg-muted-foreground",
      },
      filled: {
        true: "shadow-sm",
        false: "border-border bg-muted/30",
      },
    },
    defaultVariants: {
      filled: false,
      size: "default",
      tone: "default",
    },
  },
);

type CounterTone = NonNullable<VariantProps<typeof counterPipVariants>["tone"]>;
type CounterSize = NonNullable<VariantProps<typeof counterPipVariants>["size"]>;

export interface CounterProps
  extends Omit<React.ComponentProps<"div">, "children">,
    VariantProps<typeof counterVariants> {
  value: number;
  max: number;
  label?: React.ReactNode;
  variant?: "pips";
  tone?: CounterTone;
  size?: CounterSize;
  showValue?: boolean;
  valueLabel?: React.ReactNode;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function Counter({
  value,
  max,
  label,
  variant = "pips",
  tone = "default",
  size = "default",
  orientation = "horizontal",
  showValue = true,
  valueLabel,
  className,
  ...props
}: CounterProps) {
  const normalizedMax = Math.max(0, Math.floor(max));
  const normalizedValue = clamp(Math.floor(value), 0, normalizedMax);
  const pips = Array.from({ length: normalizedMax }, (_, index) => index + 1);
  const displayValue = valueLabel ?? `${normalizedValue}/${normalizedMax}`;

  return (
    <div
      data-slot="counter"
      data-variant={variant}
      data-orientation={orientation}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={normalizedMax}
      aria-valuenow={normalizedValue}
      aria-label={typeof label === "string" ? label : undefined}
      className={cn(counterVariants({ orientation }), className)}
      {...props}
    >
      {(label || showValue) && (
        <div className="flex min-w-0 items-center justify-between gap-2 text-xs leading-none">
          {label ? (
            <span
              data-slot="counter-label"
              className="truncate font-medium text-muted-foreground"
            >
              {label}
            </span>
          ) : (
            <span />
          )}
          {showValue ? (
            <span
              data-slot="counter-value"
              className="font-mono font-medium text-foreground"
            >
              {displayValue}
            </span>
          ) : null}
        </div>
      )}
      <div
        data-slot="counter-pips"
        className={cn("flex gap-1", orientation === "vertical" && "flex-col")}
      >
        {pips.map((pip) => (
          <CounterPip
            key={pip}
            filled={pip <= normalizedValue}
            tone={tone}
            size={size}
          />
        ))}
      </div>
    </div>
  );
}

function CounterPip({
  className,
  filled = false,
  tone = "default",
  size = "default",
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof counterPipVariants>) {
  return (
    <span
      data-slot="counter-pip"
      data-filled={filled ? "true" : "false"}
      aria-hidden="true"
      className={cn(counterPipVariants({ filled, tone, size }), className)}
      {...props}
    />
  );
}

export type { CounterSize, CounterTone };
export { Counter, CounterPip, counterPipVariants, counterVariants };
